import { useMutation, useQueryClient } from 'react-query';
import isArray from 'lodash/isArray';

import { UpdateItemGqlQuery, UpdateItemCacheKeys } from '../../../../../types';

import { updateCacheItem } from '../../actions/updateCacheItem';

import {
  ErrorType,
  parseRequestError
} from '../../../../../utils/parseRequestError';

interface CacheUpdateQueryOptions<UpdateQueryInput, ItemType> {
  action: string;
  query: UpdateItemGqlQuery;
  cacheKeys?: UpdateItemCacheKeys;
  onOptimisticUpdate?: (input: UpdateQueryInput) => null | (() => void);
  onSuccess?: (responseRecord: ItemType) => void;
  refetchInactive?: boolean;
}

interface CacheUpdateQueryContext {
  rollback?: () => void;
}

function useCacheUpdateQuery<
  UpdateQueryInput,
  UpdateQueryResponse,
  UpdateQueryError extends ErrorType,
  ItemType
>({
  action,
  query,
  cacheKeys,
  onOptimisticUpdate,
  onSuccess,
  refetchInactive = false
}: CacheUpdateQueryOptions<UpdateQueryInput, ItemType>) {
  const queryClient = useQueryClient();

  const { data, error, isLoading, mutateAsync, reset } = useMutation<
    UpdateQueryResponse,
    UpdateQueryError,
    UpdateQueryInput,
    CacheUpdateQueryContext
  >(
    (queryInput) =>
      updateCacheItem<UpdateQueryInput, UpdateQueryResponse>({
        action,
        query,
        queryInput
      }),
    {
      onMutate: (input) => {
        const rollback = onOptimisticUpdate?.(input) || undefined;

        return { rollback };
      },
      onError: (error, variables, context) => {
        context?.rollback?.();
      },
      onSuccess: (response) => {
        onSuccess?.(response?.[action]?.record);
      },
      onSettled: async (response) => {
        if (isArray(cacheKeys)) {
          cacheKeys.map((eachCacheKey) =>
            queryClient.invalidateQueries(eachCacheKey, { refetchInactive })
          );
        }
      }
    }
  );

  return {
    updateCacheQueryData: data,
    updateCacheQueryError: error,
    updateCacheQueryLoading: isLoading,
    updateCacheQueryErrorMessage: parseRequestError(error),
    updateCacheQuery: mutateAsync,
    updateCacheQueryReset: reset
  };
}

export default useCacheUpdateQuery;
